const productionWorkerUrl = 'https://iso27001-worker.wissda-internal.workers.dev/api';
export const BASE = import.meta.env.VITE_API_BASE_URL || (window.location.hostname.includes('localhost') ? '/api' : productionWorkerUrl);
let currentRole = 'Admin'

export const setApiRole = (role) => {
  currentRole = role
}

async function request(path, options = {}) {
  const token = localStorage.getItem('auth_token')
  const graphToken = localStorage.getItem('graph_token')
  const res = await fetch(`${BASE}${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      'x-user-role': currentRole,
      ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
      ...(graphToken ? { 'X-Graph-Token': graphToken } : {}),
      ...options.headers
    },
    body: options.body ? JSON.stringify(options.body) : undefined,
  })
  if (!res.ok) {
    let errorMsg = `HTTP ${res.status}`;
    try {
      const err = await res.json();
      errorMsg = err.error || errorMsg;
    } catch (e) {
      errorMsg = `Server Error (${res.status})`;
    }
    throw new Error(errorMsg);
  }
  try {
    return await res.json();
  } catch (e) {
    return { success: true }; // Fallback for empty/non-json success responses
  }
}

export const api = {
  // Assets
  getAssets: () => request('/assets'),
  createAsset: (data) => request('/assets', { method: 'POST', body: data }),
  bulkCreateAssets: (data) => request('/assets/batch', { method: 'POST', body: data }),
  updateAsset: (id, data) => request(`/assets/${id}`, { method: 'PUT', body: data }),
  deleteAsset: (id, reason) => request(`/assets/${id}`, { method: 'DELETE', body: { reason } }),

  // Risks
  getRisks: (filters = {}) => {
    const params = new URLSearchParams()
    if (filters.status) params.append('status', Array.isArray(filters.status) ? filters.status.join(',') : filters.status)
    if (filters.from) params.append('from', filters.from)
    if (filters.to) params.append('to', filters.to)
    const qs = params.toString()
    return request(`/risks${qs ? '?' + qs : ''}`)
  },
  getRisk: (id) => request(`/risks/${id}`),
  getRiskHistory: (id) => request(`/risks/${id}/history`),
  createRisk: (data) => request('/risks', { method: 'POST', body: data }),
  bulkCreateRisks: (data) => request('/risks/batch', { method: 'POST', body: data }),
  updateRisk: (id, data) => request(`/risks/${id}`, { method: 'PUT', body: data }),
  deleteRisk: (id, reason) => request(`/risks/${id}`, { method: 'DELETE', body: { reason } }),

  // Vulnerabilities
  getVulnerabilities: () => request('/vulnerabilities'),
  createVulnerability: (data) => request('/vulnerabilities', { method: 'POST', body: data }),
  bulkCreateVulnerabilities: (data) => request('/vulnerabilities/batch', { method: 'POST', body: data }),
  updateVulnerability: (id, data) => request(`/vulnerabilities/${id}`, { method: 'PUT', body: data }),
  deleteVulnerability: (id, reason) => request(`/vulnerabilities/${id}`, { method: 'DELETE', body: { reason } }),

  // Controls (Annex A & Security)
  getAnnexControls: () => request('/annex-controls'),
  updateAnnexControl: (id, data) => request(`/annex-controls/${id}`, { method: 'PUT', body: data }),
  getControls: () => request('/security-controls'), 
  createControl: (data) => request('/security-controls', { method: 'POST', body: data }),
  bulkCreateControls: (data) => request('/security-controls/batch', { method: 'POST', body: data }),
  updateControl: (id, data) => request(`/security-controls/${id}`, { method: 'PUT', body: data }),
  deleteControl: (id, reason) => request(`/security-controls/${id}`, { method: 'DELETE', body: { reason } }),
  
  getSecurityControls: () => request('/security-controls'),
  createSecurityControl: (data) => request('/security-controls', { method: 'POST', body: data }),
  bulkCreateSecurityControls: (data) => request('/security-controls/batch', { method: 'POST', body: data }),
  updateSecurityControl: (id, data) => request(`/security-controls/${id}`, { method: 'PUT', body: data }),
  deleteSecurityControl: (id, reason) => request(`/security-controls/${id}`, { method: 'DELETE', body: { reason } }),

  // Processes
  getProcesses: () => request('/processes'),
  createProcess: (data) => request('/processes', { method: 'POST', body: data }),
  updateProcess: (id, data) => request(`/processes/${id}`, { method: 'PUT', body: data }),
  deleteProcess: (id, reason) => request(`/processes/${id}`, { method: 'DELETE', body: { reason } }),

  // Audits
  getAudits: () => request('/audits'),
  createAudit: (data) => request('/audits', { method: 'POST', body: data }),
  getAuditFindings: (auditId) => request(`/audits/${auditId}/findings`),
  createAuditFinding: (auditId, data) => request(`/audits/${auditId}/findings`, { method: 'POST', body: data }),

  // NC & CAPA
  getNonConformities: () => request('/non-conformities'),
  createNonConformity: (data) => request('/non-conformities', { method: 'POST', body: data }),
  updateNonConformity: (id, data) => request(`/non-conformities/${id}`, { method: 'PUT', body: data }),
  deleteNonConformity: (id, reason) => request(`/non-conformities/${id}`, { method: 'DELETE', body: { reason } }),

  // Training
  getTrainings: () => request('/trainings'),
  createTraining: (data) => request('/trainings', { method: 'POST', body: data }),

  // Documents
  getDocuments: () => request('/documents'),
  createDocument: (data) => request('/documents', { method: 'POST', body: data }),
  updateDocument: (id, data) => request(`/documents/${id}`, { method: 'PUT', body: data }),
  deleteDocument: (id, reason) => request(`/documents/${id}`, { method: 'DELETE', body: { reason } }),
  getDocReviews: (id) => request(`/documents/${id}/reviews`),
  submitDocumentForReview: (id, reviewerId) => request(`/documents/${id}/submit`, { method: 'POST', body: { reviewerId } }),
  reviewDocument: (id, data) => request(`/documents/${id}/reviews`, { method: 'POST', body: data }),
  getPendingReviews: () => request('/reviews/pending'),

  // Recycle Bin
  getRecycleBin: () => request('/recycle-bin'),
  restoreFromBin: (id) => request(`/recycle-bin/${id}/restore`, { method: 'POST' }),
  purgeFromBin: (id) => request(`/recycle-bin/${id}`, { method: 'DELETE' }),

  // Compliance Clauses
  getClauses: () => request('/compliance-clauses'),
  updateClause: (id, data) => request(`/compliance-clauses/${id}`, { method: 'PUT', body: data }),

  // Scope
  getScope: () => request('/scope'),
  upsertScope: (data) => request('/scope', { method: 'POST', body: data }),

  // Dashboard & Work
  getDashboard: () => request('/dashboard'),
  getMyWork: (owner_id) => request(`/my-work${owner_id ? '?owner_id=' + owner_id : ''}`),

  // Audit Logs
  getAuditLogs: (filters = {}) => {
    const params = new URLSearchParams()
    if (filters.entity) params.append('entity', filters.entity)
    if (filters.from) params.append('from', filters.from)
    if (filters.to) params.append('to', filters.to)
    const qs = params.toString()
    return request(`/audit-logs${qs ? '?' + qs : ''}`)
  },

  // Users
  getUsers: async () => {
    const users = await request('/users');
    return users.map(u => ({ ...u, role: u.role === 'Employee' ? 'Contributor' : u.role }));
  },

  // Generic request
  request
}
